import { useState, useEffect } from 'react';
import { api } from '../../lib/api.js';
import ContactFormsCard from '../ContactFormsCard.jsx';
import NewsletterFormsCard from '../NewsletterFormsCard.jsx';
import SitePromosCard, { SiteCode } from '../SitePromosCard.jsx';
import StyleSettings from '../EmbedStyleSettings.jsx';
import { hint, input, btn, primary, panelBox } from './HubKit.jsx';

// Admin → the client → Website, and Settings → Website: everything Pulse puts
// on a client's OWN website, in one place — contact forms, newsletter sign-ups,
// website pop-ups — plus the settings they share.
//
// Dual-surface via `scope` ('admin-client' | 'my'), passed through to each part.
// `parts` says which of the three this client has (their flags); a part whose
// flag is off is simply not offered (its own routes refuse it too).
//
// Mobile-first: the part tabs scroll sideways on a phone; everything below is
// one column.
const PARTS = [
  ['contact', 'Contact forms'],
  ['newsletter', 'Newsletter sign-ups'],
  ['promos', 'Pop-ups'],
  ['settings', 'Site settings'],
];

export default function WebsiteHub({ entityId, scope = 'my', parts = { contact: true, newsletter: true, promos: true }, initial = '' }) {
  const shown = PARTS.filter(([k]) => k === 'settings' || parts[k]);
  const [part, setPart] = useState(shown.some(([k]) => k === initial) ? initial : shown[0][0]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ ...hint, margin: 0 }}>Everything Pulse puts on your own website: contact forms, newsletter sign-ups and pop-ups, managed here.</p>
      <div role="tablist" aria-label="Website" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
        {shown.map(([k, l]) => {
          const on = part === k;
          return (
            <button key={k} type="button" role="tab" aria-selected={on} onClick={() => setPart(k)}
              style={{ minHeight: 40, padding: '0 14px', borderRadius: 999, border: `1px solid ${on ? 'transparent' : 'var(--hairline)'}`, background: on ? 'var(--text, #16181d)' : 'var(--bg)', color: on ? 'var(--bg, #fff)' : 'inherit', fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              {l}
            </button>
          );
        })}
      </div>
      {/* key: switching parts starts each one at its list */}
      {part === 'contact' && <ContactFormsCard key="contact" entityId={entityId} scope={scope} />}
      {part === 'newsletter' && <NewsletterFormsCard key="newsletter" entityId={entityId} scope={scope} />}
      {part === 'promos' && <SitePromosCard key="promos" entityId={entityId} scope={scope} />}
      {part === 'settings' && <SiteSettings entityId={entityId} scope={scope} parts={parts} />}
    </div>
  );
}

// What the three parts share: the one site code, one look applied to all of
// them, and the websites allowed to submit the forms. "Apply to all" writes the
// setting onto every form and pop-up (each can still be changed on its own).
function SiteSettings({ entityId, scope, parts }) {
  const apiScope = scope === 'my' ? 'my' : 'admin';
  const [promos, setPromos] = useState(null);
  const [look, setLook] = useState({ style: {}, brandColor: '' });
  const [domains, setDomains] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => { if (parts.promos) api.sitePromos(entityId, apiScope).then((v) => setPromos(v && !v.error ? v : null)).catch(() => setPromos(null)); }, [entityId, apiScope, parts.promos]);

  const everything = async () => {
    const [ct, nl, pr] = await Promise.all([
      parts.contact ? api.contactForms(entityId, apiScope).then((v) => v.forms || []).catch(() => []) : [],
      parts.newsletter ? api.newsletterForms(entityId, apiScope).then((v) => v.forms || []).catch(() => []) : [],
      parts.promos ? api.sitePromos(entityId, apiScope).then((v) => v.promos || []).catch(() => []) : [],
    ]);
    return { ct, nl, pr };
  };
  const applyLook = async () => {
    const { ct, nl, pr } = await everything();
    const n = ct.length + nl.length + pr.length;
    if (!n) { setMsg('There are no forms or pop-ups to apply it to yet.'); return; }
    if (!window.confirm(`Apply this look to ${ct.length} contact form(s), ${nl.length} sign-up form(s) and ${pr.length} pop-up(s)? Their current look is replaced.`)) return;
    setBusy('look'); setMsg('');
    const st = look.style || {};
    try {
      for (const f of ct) await api.contactFormUpdate(entityId, apiScope, f.id, { style: st, brandColor: look.brandColor });
      for (const f of nl) await api.newsletterFormUpdate(entityId, apiScope, f.id, { style: st, brandColor: look.brandColor });
      // A pop-up's look has its own shape: the corners, button style and colours map across.
      for (const p of pr) {
        await api.sitePromoUpdate(entityId, apiScope, p.id, { look: {
          ...p.look,
          ...(typeof st.radius === 'number' ? { radius: st.radius } : {}),
          ...(typeof st.fieldRadius === 'number' ? { buttonRadius: st.fieldRadius } : {}),
          buttonStyle: st.buttonStyle === 'caps' ? 'caps' : 'normal',
          ...(look.brandColor ? { accent: look.brandColor } : {}),
          ...(st.ink ? { fg: st.ink } : {}),
          ...(st.surface ? { bg: st.surface } : {}),
        } });
      }
      setMsg(`Done: the look is now on all ${n}.`);
    } catch (e) { setMsg(String(e.message || e)); }
    setBusy('');
  };
  const applyDomains = async () => {
    const { ct, nl } = await everything();
    if (!ct.length && !nl.length) { setMsg('There are no forms to apply it to yet.'); return; }
    if (!window.confirm(`Set the allowed websites on ${ct.length} contact form(s) and ${nl.length} sign-up form(s)?`)) return;
    setBusy('domains'); setMsg('');
    try {
      for (const f of ct) await api.contactFormUpdate(entityId, apiScope, f.id, { allowedDomains: domains });
      for (const f of nl) await api.newsletterFormUpdate(entityId, apiScope, f.id, { allowedDomains: domains });
      setMsg(`Done: ${ct.length + nl.length} forms now accept only ${domains.trim() || 'any website'}.`);
    } catch (e) { setMsg(String(e.message || e)); }
    setBusy('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={panelBox}>
        <b style={{ fontSize: 14 }}>Site code</b>
        {parts.promos && promos ? <SiteCode data={promos} /> : (
          <p style={{ ...hint, margin: '6px 0 0' }}>The site code is for website pop-ups, which aren’t switched on for this client.</p>
        )}
        <p style={{ ...hint, margin: '8px 0 0' }}>Contact and sign-up forms go exactly where you want them on a page: each has its own one-line code under its <b>Add to website</b> tab.</p>
      </div>

      <div style={panelBox}>
        <b style={{ fontSize: 14 }}>One look for everything</b>
        <p style={{ ...hint, margin: '6px 0 0' }}>Choose a look once and apply it to every contact form, sign-up form and pop-up, so they match your website. Each one can still be adjusted on its own afterwards.</p>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, margin: '10px 0 4px' }}>Button colour
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="color" style={{ width: 48, height: 40, border: '1px solid var(--hairline)', borderRadius: 9, background: 'var(--bg)', padding: 2 }}
              value={/^#[0-9a-f]{6}$/i.test(look.brandColor || '') ? look.brandColor : '#111111'} onChange={(e) => setLook((l) => ({ ...l, brandColor: e.target.value }))} />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{look.brandColor || 'Your branding colour'}</span>
          </div>
        </label>
        <StyleSettings style={look.style} onChange={(v) => setLook((l) => ({ ...l, style: v }))} onPreset={(p) => setLook({ style: p.style, brandColor: p.brandColor })} />
        <button type="button" style={{ ...btn, ...primary, marginTop: 10 }} disabled={!!busy} onClick={applyLook}>{busy === 'look' ? 'Applying…' : 'Apply to all forms and pop-ups'}</button>
      </div>

      {(parts.contact || parts.newsletter) && (
        <div style={panelBox}>
          <b style={{ fontSize: 14 }}>Allowed websites</b>
          <p style={{ ...hint, margin: '6px 0 0' }}>Only accept enquiries and sign-ups sent from these websites. Each domain also covers its subdomains; blank means any website. The hosted links always work.</p>
          <input style={{ ...input, marginTop: 6 }} value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="e.g. kappafuturfestival.it" aria-label="Allowed websites" />
          <button type="button" style={{ ...btn, marginTop: 8 }} disabled={!!busy} onClick={applyDomains}>{busy === 'domains' ? 'Applying…' : 'Apply to all forms'}</button>
        </div>
      )}
      {msg && <div style={{ fontSize: 13, lineHeight: 1.5 }}>{msg}</div>}
    </div>
  );
}
